import { Position, useReactFlow } from '@xyflow/react';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTrashNode } from './commons/trashNode';
import { FlowHandle } from './commons/flowHandle';
import {
  GlobalNodeIndicator,
  GlobalNodeMenuItem
} from './commons/globalNodeBadge';
import './style.scss';
import { UilTrashAlt } from '@iconscout/react-unicons';
import SimpleLabelTextBox from 'pub-components/ui-controls/ui-components/SimpleLabelTextbox';
import { useEdges } from '@xyflow/react';
import { Button, Collapse } from 'react-bootstrap';
import { IconButton, Menu, MenuItem } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import CallSplitIcon from '@material-ui/icons/CallSplit';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { useCopyPaste } from '../workflow-hooks/useCopyPaste';
import { handleClipboardKeyDown } from '../helper';
import { selectWorkflowType } from 'store/slices/chatbot/chatbotSlice';
import { workflowPromptType, workflowType, Enums } from 'services/dropdown-enums';
import { useWorkflowTypeAdapter } from '../../adapters/workflowTypeAdapter';
import {
  contactFieldsForMessageType,
  DEFAULT_CONTACT_VISITOR_RESPONSE_TYPE,
  getConversationMessageTypeOptions,
  isChoiceMessageType,
  normalizeMessageTypeForQuickReplies,
  WORKFLOW_CONTACT_FIELD_OPTIONS
} from '../../constants/conversation-message-types';

const DEFAULT_CALENDAR_QUESTION_TYPE_SETTINGS = {
  disableDays: false,
  daysNumber: 60,
  daysType: 1, // 1 = Days, 2 = Month, 3 = Year
  isPrevDaysDisable: true
};

export default function ConversationNode({ id, data, editable = true }) {
  const { setNodes, setEdges } = useReactFlow();
  const trashNode = useTrashNode();
  const edges = useEdges();
  const { duplicateNodes, duplicateNodesWithChildren } = useCopyPaste({
    bindShortcuts: false,
    manageDomEvents: false
  });

  const workflowBuildMode = useSelector(selectWorkflowType);
  const adapter = useWorkflowTypeAdapter(workflowBuildMode);
  const { showPromptType, showTransitions, showUserOptionHandles, showSkippable } =
    adapter.getConversationNodeConfig();

  const [text, setText] = useState(data?.text || '');

  const [promptType, setPromptType] = useState(
    data?.promptType || workflowPromptType.Prompt
  );

  const [items, setItems] = useState(
    data?.transitions || [{ id: `transition-${id}-0`, value: '' }]
  );

  const [userOptions, setUserOptions] = useState(data?.userOptions || []);
  const [isSkippable, setIsSkippable] = useState(Boolean(data?.isSkippable));
  const [isRequired, setIsRequired] = useState(Boolean(data?.isRequired));
  const [messageType, setMessageType] = useState(() =>
    normalizeMessageTypeForQuickReplies(
      data?.messageType !== undefined && data?.messageType !== null
        ? data.messageType
        : Enums.WFQuestionType.None,
      (data?.userOptions || []).length > 0
    )
  );
  const [visitorResponseType, setVisitorResponseType] = useState(() =>
    data?.visitorResponseType != null
      ? Number(data.visitorResponseType)
      : DEFAULT_CONTACT_VISITOR_RESPONSE_TYPE
  );
  const [useOptionsAsLeadCategory, setUseOptionsAsLeadCategory] = useState(
    Boolean(data?.useOptionsAsLeadCategory)
  );
  const [enableConsent, setEnableConsent] = useState(
    Boolean(data?.enableConsent)
  );
  const [consentPromptText, setConsentPromptText] = useState(
    data?.consentPromptText || ''
  );
  const [calendarSettings, setCalendarSettings] = useState(() => ({
    ...DEFAULT_CALENDAR_QUESTION_TYPE_SETTINGS,
    ...(data?.calendarQuestionTypeSettings || {})
  }));
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const hasQuickReplies = userOptions.length > 0;
  const messageTypeOptions = getConversationMessageTypeOptions(hasQuickReplies);
  const isContactInfoNode =
    Number(messageType) === Enums.WFQuestionType.ContactInfo;
  const isPhoneContactNode =
    isContactInfoNode &&
    Number(visitorResponseType) === Enums.VisitorResponseType.Phone;
  const showEnableConsent = adapter.isStatic && isPhoneContactNode;
  const showUseOptionsAsLeadCategory = isChoiceMessageType(messageType);
  const isCalendarNode =
    Number(messageType) === Enums.WFQuestionType.TimeCalendar ||
    Number(messageType) === Enums.WFQuestionType.CalendarDate;

  // Same shape as the legacy script's calendarQuestionTypeSettings.
  const updateCalendarSettings = patch => {
    const next = { ...calendarSettings, ...patch };
    setCalendarSettings(next);
    onChange({ calendarQuestionTypeSettings: next });
  };
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const closeMenu = () => {
    setMenuAnchorEl(null);
  };

  const openMenu = e => {
    e.preventDefault();
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };

  const handleDuplicate = e => {
    e.preventDefault();
    e.stopPropagation();
    duplicateNodes([id]);
    closeMenu();
  };

  const handleDelete = e => {
    closeMenu();
    trashNode(e, id);
  };

  const handleDuplicateWithChildren = e => {
    e.preventDefault();
    e.stopPropagation();
    duplicateNodesWithChildren([id]);
    closeMenu();
  };

  // 🔹 Sync node data
  const onChange = newData => {
    const nextMessageType =
      newData.messageType !== undefined ? newData.messageType : messageType;
    const nextVisitorResponseType =
      newData.visitorResponseType !== undefined
        ? Number(newData.visitorResponseType)
        : visitorResponseType;
    const contactFields = contactFieldsForMessageType(
      nextMessageType,
      nextVisitorResponseType
    );

    setNodes(nds =>
      nds.map(node =>
        node.id === id
          ? {
              ...node,
              data: {
                ...node.data,
                text,
                transitions: items,
                userOptions,
                promptType,
                isRequired,
                messageType: nextMessageType,
                useOptionsAsLeadCategory,
                enableConsent,
                consentPromptText,
                ...newData,
                messageType: nextMessageType,
                ...contactFields
              }
            }
          : node
      )
    );
  };

  // 🔹 Add transition
  const addItem = () => {
    setItems(prev => {
      const maxIndex = prev.reduce((max, item) => {
        const match = item.id.match(new RegExp(`^transition-${id}-(\\d+)$`));
        if (!match) return max;
        const num = parseInt(match[1], 10);
        return Number.isNaN(num) ? max : Math.max(max, num);
      }, -1);

      const nextIndex = maxIndex + 1;

      const next = [
        ...prev,
        { id: `transition-${id}-${nextIndex}`, value: '' }
      ];
      onChange({
        text,
        transitions: next,
        userOptions,
        promptType,
        isRequired
      });
      return next;
    });
  };

  // 🔹 Update transition
  const updateItem = (index, value) => {
    setItems(prev => {
      const next = prev.map((item, i) =>
        i === index ? { ...item, value } : item
      );
      onChange({
        text,
        transitions: next,
        userOptions,
        promptType,
        isRequired
      });
      return next;
    });
  };

  // 🔹 Remove transition
  const removeItem = index => {
    setItems(prev => {
      const deleted = prev[index];

      const next = prev.filter((_, i) => i !== index);

      // // Reindex but DO NOT add/remove edges for others
      // const reindexed = next.map((item, i) => ({
      //   ...item,
      //   id: `transition-${id}-${i}`
      // }));

      setEdges(eds =>
        eds.filter(
          e => e.sourceHandle !== deleted.id && e.targetHandle !== deleted.id
        )
      );

      onChange({
        text,
        transitions: next,
        userOptions,
        promptType,
        isRequired
      });

      return next;
    });
  };

  // 🔹 Add user option
  const addUserOption = () => {
    setUserOptions(prev => {
      const maxIndex = prev.reduce((max, item) => {
        const match = item.id.match(new RegExp(`^userOption-${id}-(\\d+)$`));
        if (!match) return max;
        const num = parseInt(match[1], 10);
        return Number.isNaN(num) ? max : Math.max(max, num);
      }, -1);

      const nextIndex = maxIndex + 1;

      const next = [
        ...prev,
        { id: `userOption-${id}-${nextIndex}`, value: '' }
      ];
      onChange({
        text,
        transitions: items,
        userOptions: next,
        promptType,
        isRequired
      });
      return next;
    });
  };

  // 🔹 Update user option
  const updateUserOption = (index, value) => {
    setUserOptions(prev => {
      const next = prev.map((item, i) =>
        i === index ? { ...item, value } : item
      );
      onChange({
        text,
        transitions: items,
        userOptions: next,
        promptType,
        isRequired
      });
      return next;
    });
  };

  // 🔹 Remove user option
  const removeUserOption = index => {
    setUserOptions(prev => {
      const deleted = prev[index];

      const next = prev.filter((_, i) => i !== index);

      setEdges(eds =>
        eds.filter(
          e => e.sourceHandle !== deleted.id && e.targetHandle !== deleted.id
        )
      );

      onChange({
        text,
        transitions: items,
        userOptions: next,
        promptType,
        isRequired
      });

      return next;
    });
  };

  const IsValidConnection = connection => {
    // limit 1 connection per source handle
    if (edges.some(edge => edge.sourceHandle === connection.sourceHandle))
      return false;

    return true;
  };

  // 🔹 Persist transitions on first mount
  useEffect(() => {
    if (!data?.transitions || data.transitions.length === 0) {
      onChange({
        text,
        transitions: items,
        userOptions,
        promptType,
        isRequired
      });
    }
    if (!data?.userOptions || data.userOptions.length === 0) {
      onChange({
        text,
        transitions: items,
        userOptions,
        promptType,
        isRequired
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const normalized = normalizeMessageTypeForQuickReplies(
      messageType,
      hasQuickReplies
    );
    if (normalized !== messageType) {
      setMessageType(normalized);
      const clearedLeadCategoryFlag = isChoiceMessageType(normalized)
        ? useOptionsAsLeadCategory
        : false;
      if (!clearedLeadCategoryFlag && useOptionsAsLeadCategory) {
        setUseOptionsAsLeadCategory(false);
      }
      onChange({
        messageType: normalized,
        useOptionsAsLeadCategory: clearedLeadCategoryFlag
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasQuickReplies]);

  // Persist default contact field when messageType is ContactInfo but fields are missing.
  useEffect(() => {
    if (Number(messageType) !== Enums.WFQuestionType.ContactInfo) {
      return;
    }

    const resolvedType =
      data?.visitorResponseType != null
        ? Number(data.visitorResponseType)
        : DEFAULT_CONTACT_VISITOR_RESPONSE_TYPE;

    if (data?.visitorResponseType == null || data?.infoToExtract == null) {
      setVisitorResponseType(resolvedType);
      onChange({
        visitorResponseType: resolvedType,
        messageType: Enums.WFQuestionType.ContactInfo
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rehydrate consent fields after save/refresh (useState only reads data on first mount).
  useEffect(() => {
    setEnableConsent(Boolean(data?.enableConsent));
    setConsentPromptText(
      typeof data?.consentPromptText === 'string' ? data.consentPromptText : ''
    );
  }, [data?.enableConsent, data?.consentPromptText]);

  // SimpleLabelTextBox calls handleChange(event, field, value) — use the value arg.
  const onConsentPromptChange = (e, _field, value) => {
    const next = value != null ? value : e?.target?.value ?? '';
    setConsentPromptText(next);
    onChange({ consentPromptText: next });
  };

  return (
    <dl className="mt-3" style={{ width: '290px' }}>
      {/* Left (incoming) handle */}
      <FlowHandle
        type="target"
        position={Position.Left}
        id={`${id}-target`}
        editable={editable}
        isConnectable={true}
      />
      <div
        className="card shadow-sm border-gray rounded-10"
        style={{ backgroundColor: '#F9F9F9' }}
      >
        <div
          className="d-flex align-items-center px-4 node-header"
          style={{ backgroundColor: '#E5EBFF' }}
        >
          <div className="d-flex mr-auto ml-2">
            <dt
              className="text-truncate fw-semibold fs-6"
              style={{ color: '#4F46E5' }}
            >
              Conversation Node
              <GlobalNodeIndicator isGlobal={data?.isGlobal} />
            </dt>
          </div>
          <div className="ms-auto">
            <IconButton
              onClick={openMenu}
              className="header-delete-icon"
              title="Conversation actions"
              disabled={!editable}
              size="small"
            >
              <MoreVertIcon style={{ color: '#4F46E5' }} />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl}
              open={isMenuOpen}
              onClose={closeMenu}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
              getContentAnchorEl={null}
            >
              <GlobalNodeMenuItem
                nodeId={id}
                editable={editable}
                onClick={closeMenu}
              />
              <MenuItem onClick={handleDuplicate}>
                <FileCopyOutlinedIcon
                  fontSize="small"
                  style={{ marginRight: '8px', color: '#4F46E5' }}
                />
                Duplicate
              </MenuItem>
              <MenuItem onClick={handleDuplicateWithChildren}>
                <CallSplitIcon
                  fontSize="small"
                  style={{ marginRight: '8px', color: '#4F46E5' }}
                />
                Duplicate with Children
              </MenuItem>
              <MenuItem onClick={handleDelete}>
                <DeleteOutlineIcon
                  fontSize="small"
                  style={{ marginRight: '8px', color: '#dc3545' }}
                />
                Delete Node
              </MenuItem>
            </Menu>
          </div>
        </div>

        <div className="card-body p-2">
          {/* Conversation type */}
          <div className={`${!showPromptType && 'mb-3'}`}>
            {showPromptType && (
              <fieldset className="mb-3">
                <div className="d-inline-flex gap-1 p-1" style={{}}>
                  <label
                    className={`form-check-label px-3 py-1 rounded-22 
                  toggle-option ${
                    promptType == workflowPromptType.Prompt ? 'selected' : ''
                  }`}
                  >
                    <input
                      type="radio"
                      name="conversationType"
                      value={workflowPromptType.Prompt}
                      checked={promptType == workflowPromptType.Prompt}
                      onChange={() => {
                        setPromptType(workflowPromptType.Prompt);
                        onChange({ promptType: workflowPromptType.Prompt });
                      }}
                      className="form-check-input d-none"
                      disabled={!editable}
                    />
                    Prompt
                  </label>
                  <label
                    className={`form-check-label px-3 py-1 rounded-22 
                  toggle-option ${
                    promptType == workflowPromptType.SayExactly
                      ? 'selected'
                      : ''
                  }`}
                  >
                    <input
                      type="radio"
                      name="conversationType"
                      value={workflowPromptType.SayExactly}
                      checked={promptType == workflowPromptType.SayExactly}
                      onChange={() => {
                        setPromptType(workflowPromptType.SayExactly);
                        onChange({ promptType: workflowPromptType.SayExactly });
                      }}
                      className="form-check-input d-none"
                      disabled={!editable}
                    />
                    Say Exactly
                  </label>
                </div>
              </fieldset>
            )}
          </div>
          {/* Textarea */}
          <div className="mb-3 position-relative">
            <SimpleLabelTextBox
              // heading={'Prompt'}
              dataProperty={text}
              maxRows={10}
              minRows={2}
              multiline
              editable={editable}
              id={`text-${id}`}
              // error={data && !promptText && 'Should not be empty'}
              handleChange={e => {
                setText(e.target.value);
                onChange({ text: e.target.value });
              }}
              onkeyPress={handleClipboardKeyDown}
              inputClasses="custom-scroll"
              placeholder={
                promptType === workflowPromptType.Prompt
                  ? 'Write a scenario'
                  : 'Write a conversation'
              }
              inputStyles={{ backgroundColor: 'white', padding: '10px' }}
              inputPropClasses="custom-placeholder"
            />
            {/* Default flow handle from text box */}
            <FlowHandle
              type="source"
              position={Position.Right}
              id={`transition-${id}-0-default`}
              style={{
                right: '-10px',
                top: '50%'
                // transform: "translateY(-50%)",
                // background: "#0d6efd", // Indigo tone for distinction
                // width: "8px",
                // height: "8px",
              }}
              isConnectable={true}
              editable={editable}
              isValidConnection={IsValidConnection}
            />
          </div>
          <div className="px-4 pb-2">
            <label
              className="small fw-medium label-text d-block mb-1 px-4"
              htmlFor={`message-type-${id}`}
            >
              Response type
            </label>
            <div className="px-4">
              <select
                id={`message-type-${id}`}
                className="form-select form-select-sm"
                value={messageType}
                disabled={!editable}
                onChange={e => {
                  const value = Number(e.target.value);
                  setMessageType(value);
                  const nextUseAsLeadCategory = isChoiceMessageType(value)
                    ? useOptionsAsLeadCategory
                    : false;
                  if (nextUseAsLeadCategory !== useOptionsAsLeadCategory) {
                    setUseOptionsAsLeadCategory(nextUseAsLeadCategory);
                  }
                  if (value === Enums.WFQuestionType.ContactInfo) {
                    const nextType =
                      visitorResponseType || DEFAULT_CONTACT_VISITOR_RESPONSE_TYPE;
                    setVisitorResponseType(nextType);
                    const nextEnableConsent =
                      nextType === Enums.VisitorResponseType.Phone
                        ? enableConsent
                        : false;
                    const nextConsentPrompt = nextEnableConsent
                      ? consentPromptText
                      : '';
                    setEnableConsent(nextEnableConsent);
                    setConsentPromptText(nextConsentPrompt);
                    onChange({
                      messageType: value,
                      visitorResponseType: nextType,
                      useOptionsAsLeadCategory: nextUseAsLeadCategory,
                      enableConsent: nextEnableConsent,
                      consentPromptText: nextConsentPrompt
                    });
                  } else {
                    setEnableConsent(false);
                    setConsentPromptText('');
                    onChange({
                      messageType: value,
                      useOptionsAsLeadCategory: nextUseAsLeadCategory,
                      enableConsent: false,
                      consentPromptText: ''
                    });
                  }
                }}
              >
                {messageTypeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {isCalendarNode && (
            <div className="px-4 pb-2">
              <div className="form-check ps-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`calendar-disable-days-${id}`}
                  checked={Boolean(calendarSettings.disableDays)}
                  disabled={!editable}
                  onChange={e =>
                    updateCalendarSettings({ disableDays: e.target.checked })
                  }
                />
                <label
                  className="form-check-label small"
                  htmlFor={`calendar-disable-days-${id}`}
                >
                  Disable Days
                </label>
              </div>
              {calendarSettings.disableDays && (
                <div className="px-2 pt-2">
                  <div className="d-flex gap-2 mb-2">
                    <input
                      type="number"
                      min="0"
                      className="form-control form-control-sm"
                      aria-label="No of Days/Month/Year"
                      value={calendarSettings.daysNumber}
                      disabled={!editable}
                      onChange={e =>
                        updateCalendarSettings({
                          daysNumber: Math.max(0, Number(e.target.value) || 0)
                        })
                      }
                    />
                    <select
                      className="form-select form-select-sm"
                      aria-label="Days type"
                      value={calendarSettings.daysType}
                      disabled={!editable}
                      onChange={e =>
                        updateCalendarSettings({
                          daysType: Number(e.target.value)
                        })
                      }
                    >
                      <option value={1}>Days</option>
                      <option value={2}>Month</option>
                      <option value={3}>Year</option>
                    </select>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      id={`calendar-prev-days-${id}`}
                      name={`calendar-days-selection-${id}`}
                      checked={Boolean(calendarSettings.isPrevDaysDisable)}
                      disabled={!editable}
                      onChange={() =>
                        updateCalendarSettings({ isPrevDaysDisable: true })
                      }
                    />
                    <label
                      className="form-check-label small"
                      htmlFor={`calendar-prev-days-${id}`}
                    >
                      Disable Previous Days
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      id={`calendar-next-days-${id}`}
                      name={`calendar-days-selection-${id}`}
                      checked={!calendarSettings.isPrevDaysDisable}
                      disabled={!editable}
                      onChange={() =>
                        updateCalendarSettings({ isPrevDaysDisable: false })
                      }
                    />
                    <label
                      className="form-check-label small"
                      htmlFor={`calendar-next-days-${id}`}
                    >
                      Disable Next Days
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}
          {showUseOptionsAsLeadCategory && (
            <div className="px-4 pb-2">
              <div className="form-check ps-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`use-options-as-lead-category-${id}`}
                  checked={useOptionsAsLeadCategory}
                  disabled={!editable}
                  onChange={e => {
                    const checked = e.target.checked;
                    setUseOptionsAsLeadCategory(checked);
                    onChange({ useOptionsAsLeadCategory: checked });
                  }}
                />
                <label
                  className="form-check-label small"
                  htmlFor={`use-options-as-lead-category-${id}`}
                >
                  Use options as lead category
                </label>
              </div>
            </div>
          )}
          {isContactInfoNode && (
            <div className="px-4 pb-2">
              <label
                className="small fw-medium label-text d-block mb-1 px-4"
                htmlFor={`contact-field-${id}`}
              >
                Contact field
              </label>
              <div className="px-4">
                <select
                  id={`contact-field-${id}`}
                  className="form-select form-select-sm"
                  value={visitorResponseType}
                  disabled={!editable}
                  onChange={e => {
                    const value = Number(e.target.value);
                    setVisitorResponseType(value);
                    const nextEnableConsent =
                      value === Enums.VisitorResponseType.Phone
                        ? enableConsent
                        : false;
                    const nextConsentPrompt = nextEnableConsent
                      ? consentPromptText
                      : '';
                    setEnableConsent(nextEnableConsent);
                    setConsentPromptText(nextConsentPrompt);
                    onChange({
                      visitorResponseType: value,
                      enableConsent: nextEnableConsent,
                      consentPromptText: nextConsentPrompt
                    });
                  }}
                >
                  {WORKFLOW_CONTACT_FIELD_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {showEnableConsent && (
            <div className="px-4 pb-2">
              <div className="form-check ps-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`enable-consent-${id}`}
                  checked={enableConsent}
                  disabled={!editable}
                  onChange={e => {
                    const checked = e.target.checked;
                    setEnableConsent(checked);
                    if (!checked) {
                      setConsentPromptText('');
                      onChange({
                        enableConsent: false,
                        consentPromptText: ''
                      });
                    } else {
                      onChange({ enableConsent: true });
                    }
                  }}
                />
                <label
                  className="form-check-label small"
                  htmlFor={`enable-consent-${id}`}
                >
                  Enable opt-in consent after phone
                </label>
              </div>
              {enableConsent && (
                <div className="px-2 mt-2">
                  <label
                    className="small fw-medium label-text d-block mb-1"
                    htmlFor={`consent-prompt-${id}`}
                  >
                    Consent message
                  </label>
                  <SimpleLabelTextBox
                    field={`consent-prompt-${id}`}
                    dataProperty={consentPromptText}
                    editable={editable}
                    handleChange={onConsentPromptChange}
                    onkeyPress={handleClipboardKeyDown}
                    placeholder="Would you like to receive text messages from us?"
                    inputStyles={{ backgroundColor: 'white' }}
                    inputPropStyle={{ padding: '10px' }}
                  />
                </div>
              )}
            </div>
          )}
          {/* User Options */}
          <div className="px-4 pb-3" style={{ marginBottom: '10px' }}>
            {userOptions.length > 0 && (
              <div>
                <dt className="text-truncate small fw-medium mt-3 px-4 label-text">
                  Quick Replies
                </dt>
                <div>
                  {userOptions != null &&
                    userOptions?.map((item, index) => (
                      <div
                        key={item.id}
                        className="mt-1 d-flex align-items-center gap-4 position-relative fw-semibold mb-2"
                      >
                        {showUserOptionHandles && (
                          <FlowHandle
                            type="source"
                            position={Position.Right}
                            id={item.id}
                            style={{
                              right: '-13px',
                              top: '50%'
                            }}
                            editable={editable}
                            isConnectable={true}
                            isValidConnection={IsValidConnection}
                          />
                        )}
                        <SimpleLabelTextBox
                          dataProperty={item.value}
                          editable={editable}
                          handleChange={e =>
                            updateUserOption(index, e.target.value)
                          }
                          onkeyPress={handleClipboardKeyDown}
                          placeholder="User Option"
                          inputStyles={{ backgroundColor: 'white' }}
                          inputPropStyle={{ padding: '10px' }}
                          styles={{ flex: 1 }}
                        />
                        <Button
                          variant="transparent"
                          type="button"
                          className="btn btn-sm btn-link text-danger ms-2 p-0 ml-1"
                          onClick={() => removeUserOption(index)}
                          title="Remove"
                          disabled={!editable}
                        >
                          <UilTrashAlt
                            style={{
                              width: '20px',
                              height: '20px',
                              color: '#dc3545'
                            }}
                          />
                          {/* <Trash size={14} /> */}
                        </Button>
                      </div>
                    ))}
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={addUserOption}
              className="mt-3 fw-semibold align-middle border-0 rounded-1 shadow-sm condition-button p-1"
              disabled={!editable}
            >
              + Add Quick Replies
            </button>
            {showSkippable && (
              <div className="form-check mt-3 ps-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`isSkippable-${id}`}
                  checked={isSkippable}
                  disabled={!editable}
                  onChange={e => {
                    setIsSkippable(e.target.checked);
                    onChange({ isSkippable: e.target.checked });
                  }}
                />
                <label className="form-check-label small" htmlFor={`isSkippable-${id}`}>
                  Allow visitor to skip this question
                </label>
              </div>
            )}
          </div>
          {/* Transitions (AI workflows); static workflows branch from Quick Reply handles */}
          {showTransitions && (
            <>
              <div className="px-4 pb-3" style={{ marginBottom: '10px' }}>
                {items.length > 0 && (
                  <div>
                    <dt className="text-truncate small fw-medium mt-3 px-4 label-text">
                      Transition
                    </dt>
                    <div>
                      {items != null &&
                        items?.map((item, index) => (
                          <div
                            key={item.id}
                            className="mt-1 d-flex align-items-center gap-4 position-relative fw-semibold mb-2"
                          >
                            {/* Handle (must match transition ID) */}
                            <FlowHandle
                              type="source"
                              position={Position.Right}
                              id={item.id}
                              style={{
                                right: '-13px',
                                top: '50%'
                                // transform: "translateY(-50%)",
                                // background: "#0d6efd",
                                // width: "8px",
                                // height: "8px",
                              }}
                              editable={editable}
                            />
                            <SimpleLabelTextBox
                              dataProperty={item.value}
                              editable={editable}
                              handleChange={e =>
                                updateItem(index, e.target.value)
                              }
                              onkeyPress={handleClipboardKeyDown}
                              placeholder="Transition condition"
                              inputStyles={{ backgroundColor: 'white' }}
                              inputPropStyle={{ padding: '10px' }}
                              styles={{ flex: 1 }}
                            />
                            <Button
                              variant="transparent"
                              type="button"
                              className="btn btn-sm btn-link text-danger ms-2 p-0 ml-1"
                              onClick={() => removeItem(index)}
                              title="Remove"
                              disabled={!editable}
                            >
                              <UilTrashAlt
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  color: '#dc3545'
                                }}
                              />
                              {/* <Trash size={14} /> */}
                            </Button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={addItem}
                  className="mt-3 fw-semibold align-middle border-0 rounded-1 shadow-sm condition-button p-1"
                  disabled={!editable}
                >
                  + Add Condition
                </button>
              </div>

              <div className="px-4 pb-3 border-top pt-3 mt-2">
                <button
                  type="button"
                  className="d-flex align-items-center w-100 border-0 bg-transparent small fw-semibold text-start p-0 mb-2"
                  style={{ color: '#4F46E5' }}
                  aria-expanded={advancedOpen}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAdvancedOpen(open => !open);
                  }}
                >
                  <span className="me-2 user-select-none" aria-hidden>
                    {advancedOpen ? '▼' : '▶'}
                  </span>
                  Advanced
                </button>
                <Collapse in={advancedOpen}>
                  <div
                    className="pt-1"
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                  >
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`conversation-required-${id}`}
                        checked={isRequired}
                        onChange={e => {
                          const checked = e.target.checked;
                          setIsRequired(checked);
                          onChange({ isRequired: checked });
                        }}
                        disabled={!editable}
                      />
                      <label
                        className="form-check-label small fw-medium"
                        htmlFor={`conversation-required-${id}`}
                      >
                        Required
                      </label>
                    </div>
                  </div>
                </Collapse>
              </div>
            </>
          )}
        </div>
      </div>
    </dl>
  );
}
